import { Anchor, Badge, Breadcrumbs, Card, Table, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import Metadata from '../../components/Metadata';
import Spinner from '../../components/Spinner';
import { Page } from '../../constants/pageTitles';
import describeDiscount from '../../helpers/describeDiscount';
import useFetch from '../../helpers/useFetch';

const page = `${Page['coupon']}`;

const formatDate = (isoDate) => new Date(isoDate).toLocaleString();

const CouponHistoryPage = () => {
  const fetch = useFetch();
  const { accountId, domainId, couponId } = useParams();
  const domain = useSelector((state) => state.domain.domain);
  const currency = domain?.currency;
  const canGenerateCoupons = Boolean(domain?.capabilities?.generatedCoupons);
  const [coupon, setCoupon] = useState();
  const [definitions, setDefinitions] = useState();
  const [loadError, setLoadError] = useState();

  useEffect(() => {
    const couponUrl = `/api/admin/accounts/${accountId}/domains/${domainId}/coupons/${couponId}`;
    Promise.all([fetch(couponUrl), fetch(`${couponUrl}/history`)])
      .then(([couponResponse, historyResponse]) => {
        setCoupon(couponResponse.coupon);
        setDefinitions(historyResponse.definitions || []);
      })
      .catch(setLoadError);
  }, [fetch, accountId, domainId, couponId]);

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (!coupon || !definitions) {
    return <Spinner />;
  }

  const couponsHref = `#/accounts/${accountId}/domains/${domainId}/coupons`;
  const breadcrumbItems = [
    { title: 'Home', href: `#/accounts/${accountId}/domains/${domainId}/analytics` },
    { title: page, href: canGenerateCoupons ? `${couponsHref}?source=generated` : couponsHref },
    { title: coupon.name, href: `${couponsHref}/${couponId}/details` },
    { title: 'History' },
  ].map((item, index) =>
    item.href ? (
      <Anchor href={item.href} key={index}>
        {item.title}
      </Anchor>
    ) : (
      <Text key={index}>{item.title}</Text>
    )
  );

  return (
    <>
      <Metadata title={`${page} | ${coupon.name} | History`} />
      <Breadcrumbs mb="md">{breadcrumbItems}</Breadcrumbs>
      <Card withBorder>
        <Table withTableBorder verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Coupon Definition</Table.Th>
              <Table.Th>Start Date</Table.Th>
              <Table.Th>End Date</Table.Th>
              <Table.Th>Codes Sent</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {definitions.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text c="dimmed" ta="center">
                    No history yet.
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              definitions.map((definition) => (
                <Table.Tr key={definition.id}>
                  <Table.Td>{describeDiscount(definition.discount, { currency })}</Table.Td>
                  <Table.Td>{formatDate(definition.startedAt)}</Table.Td>
                  <Table.Td>
                    {definition.endedAt ? (
                      formatDate(definition.endedAt)
                    ) : (
                      <Badge color="green" variant="light">
                        Active
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>{definition.codesSent ?? 0}</Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Card>
    </>
  );
};

export default CouponHistoryPage;
